import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { groupBy, isNil, orderBy } from 'lodash'
import moment from 'moment'
import i18n from 'simple-react-i18n'
import StationAction from '../../actions/StationAction'
import useLocalStorage from '../../../utils/customHook/useLocalStorage'
import { grey } from '../../../utils/constants/ColorTheme'
import { INDICATOR_OBJECT_TYPE, INDICATOR_TYPE, RETURN_PERIOD_BANDS } from '../../../import/constants/IndicatorsConstants'

const weekOf = date => {
    const day = moment(date)
    return `${day.year()}-${Math.floor(day.dayOfYear() / 7)}`
}

const smoothMeasures = measures => orderBy(
    Object.values(groupBy(measures, ([date]) => weekOf(date)))
        .map(week => [week[0][0], week.reduce((acc, [, value]) => acc + value, 0) / week.length]),
    ([date]) => date,
)

const isVirtualStationBand = seriesName => RETURN_PERIOD_BANDS.some(({ code }) => i18n[code] === seriesName)

const getVirtualStationBandSeries = (bands = {}, gridIndex = 0) => RETURN_PERIOD_BANDS
    .filter(({ bound }) => bands[bound]?.length)
    .map(({ code, color, bound, above, dashed }) => ({
        name: i18n[code],
        type: 'line',
        data: bands[bound].map(([date, value]) => ({ value: [date, value] })),
        showSymbol: false,
        silent: true,
        connectNulls: true,
        color,
        lineStyle: dashed ? { color: grey, type: 'dashed', width: 1 } : { opacity: 0 },
        areaStyle: { color, origin: above ? 'end' : 'start', opacity: 1 },
        z: -1,
        xAxisIndex: gridIndex,
        yAxisIndex: gridIndex,
        tooltip: { show: false },
    }))

const useVirtualStation = (objectId, startDate, endDate, objectType = INDICATOR_OBJECT_TYPE.RESOURCE) => {
    const dispatch = useDispatch()

    const [virtualStation, setVirtualStation] = useState({})
    const [isLoaded, setIsLoaded] = useState(false)
    const [smoothed, setSmoothed] = useLocalStorage('VIRTUAL_STATION_SMOOTHED', false)

    useEffect(() => {
        if (isNil(objectId)) {
            setVirtualStation({})
            return
        }
        setIsLoaded(false)
        dispatch(StationAction.fetchVirtualStationMeasures({
            objectType,
            indicatorType: INDICATOR_TYPE.WATER_STRESS,
            objectIds: [parseInt(objectId)],
            startDate,
            endDate,
        }))
            .then(result => setVirtualStation(result ?? {}))
            .finally(() => setIsLoaded(true))
    }, [objectType, objectId, startDate, endDate])

    const measures = orderBy(virtualStation.measures ?? [], ([date]) => date)

    return {
        virtualStation: {
            ...virtualStation,
            measures: smoothed ? smoothMeasures(measures) : measures,
        },
        isLoaded,
        smoothed,
        setSmoothed,
    }
}

export { useVirtualStation, getVirtualStationBandSeries, isVirtualStationBand }